<template>
    <div class="relative">
        <loading v-if="fetchingCampaign"/>
        <div v-else-if="!campaign">{{ notFoundTitle }}</div>
        <add-campaign v-else-if="campaign.draft" :campaign="campaign"/>
        <div v-else class="max-w-960 mx-auto">
            <div class="flex">
                <div class="w-2/3 pr-8">
                    <campaign-details :campaign="campaign" class="mb-24"/>
                </div>
                <div class="w-1/3 pl-8">
                    <div
                        class="bg-white shadow p-24 rounded-6 border trans-ease border-white transition-all duration-150">
                        <h2 class="font-semibold text-body mb-4 first-letter:uppercase">
                            {{ $t('general.summary') }}
                        </h2>
                        <div class="flex justify-between items-center mb-8">
                            <p class="first-letter:uppercase font-semibold text-black">
                                {{ $t('campaigns.total_cost') }}
                            </p>
                            <p class="font-semibold text-body">
                                <currency/> {{ campaign.channels_total_price.toFixed(2) }}
                            </p>
                        </div>
                        <div class="flex justify-between items-center">
                            <p class="first-letter:uppercase font-semibold text-black">
                                {{ $t('general.status') }}
                            </p>
                            <p class="font-semibold text-body-sm">
                                <campaign-status :campaign="campaign"/>
                            </p>
                        </div>

                        <template v-if="!campaign.has_only_intern_channels">
                            <h2 class="text-body mb-4 mt-32 first-letter:uppercase font-semibold text-black">
                                {{ $t('general.statistics') }}
                            </h2>
                            <div v-for="(stat, i) in stats"
                                 :class="{'border-b-2 border-primary-5': i !== stats.length - 1, 'flex justify-between items-center ' : stat.number === 1}"
                                 class="pb-4 mb-4">
                                <p class="text-primary-80" style="max-width: 80%;">{{ stat.title }}</p>

                                <p :class="stat.number === 1 ? 'font-semibold text-body' : 'text-body-sm text-primary-80'">{{ stat.value }}</p>
                            </div>
                        </template>

                        <h3 class="mb-8 mt-32 first-letter:uppercase font-semibold text-black">
                            {{ $t('campaigns.channels') }}
                        </h3>
                        <div v-for="channel in campaign.channels" :key="channel.id"
                             class="mb-8 bg-primary-5 border border-primary-20 p-8 rounded-6 shadow-sm">
                            <div class="flex justify-between items-end">
                                <p>{{
                                        channel.name[locale] ?
                                            channel.name[locale] :
                                            channel.name[fallbackLocale]
                                   }}</p>
                                <p class="font-semibold shrink-0">
                                    <currency/> {{ channel.price.toFixed(2) }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <simple-button class="mt-16 w-full" display="secondary"
                                   @click="$router.push({ name: 'job', params: { id: campaign.job_id } })">
                       {{ $t('campaigns.view_job') }}
                    </simple-button>
                    <div v-show="false" v-if="campaign.status === 'online' && $can('campaigns.delete')" class="mt-16">
                        <button
                            :disabled="campaign.status === 'cancel'"
                            class="shadow rounded-6 bg-semantic-error p-8 w-full text-white font-semibold hover:bg-semantic-error-dark trans-ease" @click="startCancellingCampaign">
                            {{ $t('campaigns.stop_campaign') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <popup-modal-confirm v-if="cancellingCampaign" :message="$t('campaigns.stop_campaign_popup.message')" :title="$t('campaigns.stop_campaign_popup.title')"
                             :working="performingCancelRequest"
                             @close="stopCancellingCampaign"
                             @confirm="cancelCampaign"
        />
    </div>
</template>

<script>
import * as campaigns from '../services/CampaignService';
import Campaign from '../models/Campaign';
import {normalizeJson} from '../../shared/helpers';
import Loading from '../components/ui/Loading';
import SvgIcon from '../components/ui/SvgIcon';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import CampaignCharacteristics from '../components/CampaignCharacteristics';
import CampaignDetails from '../components/CampaignDetails';
import CampaignStatus from '../components/CampaignStatus';
import PopupModalConfirm from '../components/ui/PopupModalConfirm';
import _upperFirst from 'lodash/upperFirst';
import AddCampaign from '../components/ui/AddCampaign';
import SimpleButton from '../components/ui/SimpleButton';
import Currency from '../components/Currency';

export default {
    components: {
        SimpleButton,
        AddCampaign,
        PopupModalConfirm,
        CampaignStatus,
        CampaignDetails,
        CampaignCharacteristics,
        Loading,
        SvgIcon,
        Currency,
    },
    props: {
        id: [String, Number],
    },
    data() {
        return {
            justCreated: false,
            fetchingCampaign: false,
            cancellingCampaign: false,
            performingCancelRequest: false,
            notFoundTitle: _upperFirst(this.$t('general.attribute_not_found', {attribute: this.$t('campaigns.campaign')})),
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        campaign() {
            return useRepo(Campaign)
                .with('job')
                .find(this.id);
        },
        stats() {
            const stats = this.campaign.stats;

            let clicks = parseInt(stats.clicks || 0) + parseInt(stats.clicks_cpc || 0);
            let ctr = (stats.ctr || 0);
            let apply_clicks = parseInt(stats.apply_clicks || 0)

            return [
                {
                    title: _upperFirst(this.$t('campaigns.editor.channels.clicks')),
                    value: (clicks > 0 ? clicks : this.$t('campaigns.zero_stats')),
                    number: (clicks > 0 ? 1 : 0),
                },
                {
                    title: _upperFirst(this.$t('campaigns.editor.channels.ctr')),
                    value: (ctr > 0 ? (ctr * 100).toFixed(2) + '%' : this.$t('campaigns.zero_stats')),
                    number: (ctr > 0 ? 1 : 0),
                },
                {
                    title: _upperFirst(this.$t('campaigns.editor.channels.clicks_apply')),
                    value: (stats.clicks_apply > 0 ? stats.clicks_apply : this.$t('campaigns.zero_stats')),
                    number: (stats.clicks_apply > 0 ? 1 : 0),
                },
            ];
        },
        fallbackLocale() {
            return Jobtoolz.config.translatable.fallback_locale;
        },
    },
    methods: {
        startCancellingCampaign() {
            this.cancellingCampaign = true;
        },
        stopCancellingCampaign() {
            this.cancellingCampaign = false;
        },
        cancelCampaign() {
            this.performingCancelRequest = true;

            campaigns.update(this.campaign.id, {
                data: {
                    attributes: {
                        status: 'retract',
                    },
                },
            }).then(result => {
                return useRepo(Campaign).save(normalizeJson(result));
            }).then(() => {
                this.stopCancellingCampaign();
            }).finally(() => {
                this.performingCancelRequest = false;
            });
        },
    },
    mounted() {
        this.fetchingCampaign = true;

        campaigns.show(this.id).then(data => {
            return useRepo(Campaign).save(normalizeJson(data));
        }).finally(() => {
            this.fetchingCampaign = false;
        });
    },
};
</script>
